import { useEffect, useRef, useState } from 'react';
import { Loader, Table, Group } from '@mantine/core';
import Header from '../../components/util/header';
import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import { LoginState } from '../../client-redux/login/reducer';
import { formatCurrAmount, getLocationName } from '../../utils/script';
import { ICurrency } from '../../../libraries/currencies';
import FilterBar, { FilterBarFieldsData } from '../../components/filterbar/index';
import PaginationComponent from '../../components/pagination';
import { useFitTableRows } from '../../components/util/fit-table-rows-hook';
import { formatDateTimeNSecLabel, formatDateToISO } from '../../utils/date';
import { fetchPeriodsTotals, PeriodTotals } from '../../../libraries/periods';
import ShowPeriodDetails from './show-period-equipment';
import { useStatusOperDialog } from '../../context/status-oper-dialog';
import { StatusType } from '../../../libraries/zps';
import './styles.scss';

const hideColumns = {
  local: false,
  equipID: false,
  period: false,
  user: false,
  dtOpen: false,
  dtClose: false,
  amount: false,
  amountBilled: false
};

const PeriodsV2 = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const user = useSelector<AppState, LoginState>(state => state.login);
  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const region = useSelector<AppState, string>(state => state.login.user?.region || 'UTC');
  const controller = useRef(new AbortController()).current;

  const [loading, setLoading] = useState<boolean>(false);
  const [offset, setOffset] = useState<number>(0);
  /* const itemsPerPage = window.innerHeight < 600 ? 5 : Math.floor(window.innerHeight / 55 - 2); */
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: -2,
    minHeightForDynamic: 400
  });
  const [totalRows, setTotalRows] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchTrigger, setSearchTrigger] = useState<boolean>(true);
  const [periods, setPeriods] = useState<PeriodTotals[]>([]);
  const [, setTotalResults] = useState<number>(0);

  const [openDetails, setOpenDetails] = useState<boolean>(false);
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodTotals>();
  const [selectedPeriodCurrency, setSelectedPeriodCurrency] = useState<ICurrency>();

  const isPaymentEquip = user.equipmentTypes ? user.equipmentTypes.some(e => [404, 412, 442, 654].includes(e)) : false;
  const [filterBarData, setFilterBarData] = useState<FilterBarFieldsData>();

  const { handleApiError, showStatusOperDialog } = useStatusOperDialog();

  useEffect(() => {
    if (filterBarData && limit) {
      getPeriodsList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: FilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const getPeriodsList = async () => {
    if (!filterBarData || !limit) return;

    setLoading(true);
    setPeriods([]);
    setTotalRows(0);

    const shouldFetchTotalRows = offset === 0;

    fetchPeriodsTotals(
      filterBarData.entity || 0,
      filterBarData.lid || 0,
      filterBarData.eid || 0,
      offset,
      limit,
      shouldFetchTotalRows,
      controller,
      formatDateToISO(filterBarData.beginDateTime),
      formatDateToISO(filterBarData.endDateTime),
      filterBarData.period
    )
      .then(response => {
        const data = response?.data;

        if (data && data.length > 0) {
          const enriched = data.map((row: PeriodTotals) => {
            let userCollum = '-';

            if (!row.userClose && !row.dateTimeClose) {
              userCollum = row.userOpen ? `${row.userOpen.uid} - ${row.userOpen.name}` : '-';
            } else if (row.userClose) {
              userCollum = `${row.userClose.uid} - ${row.userClose.name}`;
            }

            return {
              ...row,
              amountCollum: row.totals && row.totals.length === 1 ? row.totals[0] : row.amountCollum,
              userCollum
            };
          });

          setPeriods(enriched);

          if (shouldFetchTotalRows && response?.headers?.['X-Zarph-nrows']) {
            setTotalRows(parseInt(response.headers['X-Zarph-nrows'], 10));
          }
        } else {
          showStatusOperDialog({
            type: StatusType.WARNING,
            message: lang.GLOBAL_NO_RESULTS || 'No results found'
          });
        }
      })
      .catch(err => {
        if (err?.name !== 'AbortError') {
          handleApiError(err, lang.GLOBAL_ERROR_FETCHING_DATA || 'Error fetching periods');
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Function used until pagination is not implemented on the backend
  const getTotalResults = (periods: any[]) => {
    let totalCount = 0;
    periods.forEach(array => {
      if (Array.isArray(array)) {
        totalCount += array.length;
      }
    });
    return totalCount;
  };

  useEffect(() => {
    if (periods) {
      const totalCount = getTotalResults(Object.values(periods));
      setTotalResults(totalCount);
    }
  }, [periods]);

  const changePage = (n: number) => {
    const newOffset = (n - 1) * limit;
    setCurrentPage(n);
    setOffset(newOffset);
    setSearchTrigger(true);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  // ----------------- Modal information with logs -------------------//
  const showTransactionDetails = (n: number) => {
    if (periods) {
      const selected = periods[n];
      setSelectedPeriod(selected);

      if (currencies && selected?.totals?.[0]) {
        const matchingCurrency = currencies.find(currency => currency.cid === selected.totals[0].cid);
        setSelectedPeriodCurrency(matchingCurrency);
      } else {
        setSelectedPeriodCurrency(undefined);
      }

      setOpenDetails(true);
    }
  };

  //----------------------------------------------------------------//

  return (
    <div className="periods-page">
      {openDetails && selectedPeriod && selectedPeriodCurrency ? (
        <ShowPeriodDetails
          period={selectedPeriod}
          openDetails={openDetails}
          hideBtn={() => setOpenDetails(false)}
          lang={lang}
          currency={selectedPeriodCurrency}
          isDeposit={(selectedPeriod.equiType && Math.floor(selectedPeriod.equiType / 100) === 412) || false}
        />
      ) : null}

      <Header pageTitle={lang.ENTERPRISE_EQUIP_PERIODS} />

      <FilterBar
        onDataChange={handleFilterBarOnDataChange}
        onSearch={getPeriodsList}
        fieldsUsage={{
          entity: true,
          lid: true,
          eid: true,
          beginDateTime: true,
          endDateTime: true,
          period: true
        }}
      />

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{lang.EQUIPMENT_LOCAL}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_ID}</Table.Th>
              <Table.Th>{lang.PROCESSED_GLASS_PERIOD}</Table.Th>
              <Table.Th>{lang.ENTERPRISE_EQUIP_PERIODS_DT_OPEN}</Table.Th>
              <Table.Th>{lang.ENTERPRISE_EQUIP_PERIODS_DT_CLOSE}</Table.Th>
              <Table.Th>{lang.LOGIN_USER}</Table.Th>
              {!hideColumns.amount && isPaymentEquip && (
                <>
                  <Table.Th>{lang.GLOBAL_AMOUNT}</Table.Th>
                </>
              )}
              {!hideColumns.amount && isPaymentEquip && (
                <>
                  <Table.Th>{lang.ENTERPRISE_EQUIP_PERIODS_PAYMENT_AMOUNTS_MIN}</Table.Th>
                </>
              )}
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading && (!periods || searchTrigger) ? (
              <Table.Tr>
                <Table.Td colSpan={16}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : (
              <>
                {!periods.length ? (
                  <Table.Tr>
                    <Table.Td colSpan={16} style={{ textAlign: 'center' }}>
                      {lang.GLOBAL_NO_RESULTS}
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  periods?.map((period, index) => {
                    const periodAmounts = period?.totals ?? [];
                    const matchingCurrency = currencies.find(currency => currency.cid === periodAmounts[0]?.cid);

                    return (
                      <Table.Tr key={index} onClick={() => showTransactionDetails(index)}>
                        <Table.Td>{getLocationName(period.sid)}</Table.Td>
                        <Table.Td>{period.eid}</Table.Td>
                        <Table.Td>{period.period}</Table.Td>
                        <Table.Td>{formatDateTimeNSecLabel(period.dateTimeOpen, region)}</Table.Td>
                        <Table.Td>{formatDateTimeNSecLabel(period.dateTimeClose, region)}</Table.Td>
                        <Table.Td>
                          {period.userClose ? `${period.userClose?.uid} - ${period.userClose?.name}` : '-'}
                        </Table.Td>
                        {!hideColumns.amount && isPaymentEquip && (
                          <Table.Td>
                            {periodAmounts.length === 1 && matchingCurrency
                              ? formatCurrAmount(periodAmounts[0].total || 0, matchingCurrency)
                              : '-'}
                          </Table.Td>
                        )}
                        {!hideColumns.amountBilled && (
                          <Table.Td>
                            {periodAmounts.length === 1 && matchingCurrency
                              ? formatCurrAmount(periodAmounts[0].paid || 0, matchingCurrency)
                              : '-'}
                          </Table.Td>
                        )}
                      </Table.Tr>
                    );
                  })
                )}
              </>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group>
        <PaginationComponent
          total={totalRows}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default PeriodsV2;
